/**
 * 순수 migration 엔진 (계획서 §8.3). IO 없음.
 * IndexedDB 위의 스냅샷·staging·commit·rollback은 data/migration-runner가 이 함수를 감싸서 수행한다.
 *
 * 보장:
 * - 입력 배열·객체를 절대 변경하지 않는다 (실패 시 원본이 그대로 남는다).
 * - 출력 레코드 수와 (entityType, id) 집합이 입력과 정확히 같아야 한다. 한 건이라도 다르면 실패.
 * - 모든 출력 레코드는 validate를 통과해야 한다.
 * - 데이터가 앱이 아는 버전보다 새로우면 중단한다 (다운그레이드 금지).
 */

export type VersionedRecord = { entityType: string; id: string; schemaVersion: number } & Record<string, unknown>;

export type Migration = {
  entityType: string;
  from: number;
  /** 반드시 from + 1 */
  to: number;
  description: string;
  migrate: (record: Readonly<VersionedRecord>) => VersionedRecord;
};

export type MigrationIssue = { entityType: string; id: string; code: string; detail?: string };

export type MigrationResult =
  { ok: true; records: VersionedRecord[]; migratedCount: number } | { ok: false; issues: MigrationIssue[] };

export class MigrationRegistry {
  private readonly byKey = new Map<string, Migration>();

  constructor(migrations: readonly Migration[] = []) {
    for (const m of migrations) this.add(m);
  }

  add(m: Migration): void {
    if (m.to !== m.from + 1) throw new Error(`migration은 한 단계씩만: ${m.entityType} ${m.from}->${m.to}`);
    const key = `${m.entityType}@${m.from}`;
    if (this.byKey.has(key)) throw new Error(`중복 migration: ${key}`);
    this.byKey.set(key, m);
  }

  get(entityType: string, from: number): Migration | undefined {
    return this.byKey.get(`${entityType}@${from}`);
  }
}

export function migrateRecords(
  input: readonly VersionedRecord[],
  targetVersion: number,
  registry: MigrationRegistry,
  validate: (record: VersionedRecord) => string | null,
): MigrationResult {
  const issues: MigrationIssue[] = [];
  const output: VersionedRecord[] = [];
  let migratedCount = 0;

  for (const original of input) {
    if (original.schemaVersion > targetVersion) {
      issues.push({ entityType: original.entityType, id: original.id, code: 'newer-than-app' });
      continue;
    }
    let current: VersionedRecord = structuredClone(original);
    try {
      while (current.schemaVersion < targetVersion) {
        const step = registry.get(current.entityType, current.schemaVersion);
        if (!step) {
          issues.push({
            entityType: original.entityType,
            id: original.id,
            code: 'missing-migration',
            detail: `${current.entityType}@${current.schemaVersion}`,
          });
          break;
        }
        const next = step.migrate(Object.freeze(structuredClone(current)));
        if (next.id !== original.id || next.entityType !== original.entityType) {
          issues.push({ entityType: original.entityType, id: original.id, code: 'identity-changed' });
          break;
        }
        if (next.schemaVersion !== step.to) {
          issues.push({ entityType: original.entityType, id: original.id, code: 'version-not-advanced' });
          break;
        }
        current = next;
        migratedCount++;
      }
    } catch (e) {
      issues.push({
        entityType: original.entityType,
        id: original.id,
        code: 'migration-threw',
        detail: e instanceof Error ? e.name : 'unknown',
      });
      continue;
    }
    if (current.schemaVersion !== targetVersion) continue; // issue는 위에서 기록됨
    const invalid = validate(current);
    if (invalid) {
      issues.push({
        entityType: original.entityType,
        id: original.id,
        code: 'invalid-after-migration',
        detail: invalid,
      });
      continue;
    }
    output.push(current);
  }

  if (issues.length === 0) {
    const key = (r: VersionedRecord) => `${r.entityType}:${r.id}`;
    const before = new Set(input.map(key));
    const after = new Set(output.map(key));
    if (before.size !== input.length) issues.push({ entityType: '*', id: '*', code: 'duplicate-input-ids' });
    if (output.length !== input.length || before.size !== after.size || [...before].some((k) => !after.has(k))) {
      issues.push({ entityType: '*', id: '*', code: 'record-set-mismatch' });
    }
  }

  return issues.length > 0 ? { ok: false, issues } : { ok: true, records: output, migratedCount };
}
